import { log, error } from '@/utils/debugLogger';
import {
  CompositeNodeKind,
  CompositeStrategy,
  RootNodeId,
  SafeSqlNameMaxLength,
  SpatialBindingsDefinitionVersion,
  humanizeNodeKind,
  isReservedAlias,
  isRootIdentifiedByTagKind,
  isRootWithoutRootId,
  isScalarStrategy,
  tagDiscriminatorLabel,
  toSafeSqlName,
} from './definitionVocabulary';

/**
 * The pure half of the definition model: functions OF a definition's state, holding none of it.
 *
 * The split is by reactivity, not by topic. Everything here is a plain function taking the state
 * it reads — no closure over a session, no Vue observable, no mutation. `definitionModel.js`
 * keeps the reactive state, the mutators that own it, and the factory that ties a session to a
 * target; it imports these. That line is what makes both files reviewable on their own, and it is
 * why these are testable without constructing a model at all.
 *
 * Extracted 2026-08-18 as a MOVE-ONLY refactor: no behaviour changed, no signature changed. The
 * round-trip checks that guard the serializer ran identically before and after.
 */

/** How many suffixes an alias may try before the model gives up rather than spin. */
export const AliasSuffixLimit = 1000;

export const LogPrefix = '[definitionModel]';


export function fail(message, context) {
  error(`${LogPrefix} ${message}`, context);
  throw new Error(message);
}

/**
 * Accepts either the wire's bare `'latest'` or the contract's `{kind, orderBy}` and returns the
 * nested form state stores. The bare string is accepted because that is what the authoring
 * graph's `strategies` array holds, so the relations step has one in hand already.
 *
 * `orderBy` survives only on `latest`: it is the column that decides which match is newest, and
 * the server rejects it on every other strategy.
 */
export function normalizeStrategy(strategy) {
  const kind = typeof strategy === 'string' ? strategy : strategy && strategy.kind;
  const known = Object.keys(CompositeStrategy).some((key) => CompositeStrategy[key] === kind);

  if (!known) {
    fail(`'${kind}' is not a strategy.`, { strategy, allowed: Object.values(CompositeStrategy) });
  }

  const requestedOrderBy = typeof strategy === 'string' ? null : strategy && strategy.orderBy;
  const requestedColumn = typeof strategy === 'string' ? null : strategy && strategy.column;

  return {
    kind,
    orderBy: kind === CompositeStrategy.Latest ? requestedOrderBy || null : null,
    column: kind === CompositeStrategy.List ? requestedColumn || null : null,
  };
}

/** Every alias already spoken for, lowercased. Columns and scalar edges share one namespace. */
export function collectAliases(state, exceptColumnId, exceptEdgeId) {
  const used = [];

  state.columns.forEach((column) => {
    if (column.id !== exceptColumnId) used.push(column.alias.toLowerCase());
  });

  state.edges.forEach((edge) => {
    if (edge.alias && edge.id !== exceptEdgeId) used.push(edge.alias.toLowerCase());
  });

  return used;
}

/**
 * The nearest free alias to the one asked for. A name already taken, or one the compiler emits
 * itself, gains a `_2`, `_3`, … suffix — the base is trimmed first so the result still fits the
 * server's 128-character allow-list.
 */
export function makeUniqueAlias(desired, taken, fallback) {
  const base = toSafeSqlName(desired, fallback);
  const isFree = (name) => !isReservedAlias(name) && taken.indexOf(name.toLowerCase()) === -1;

  if (isFree(base)) return base;

  for (let attempt = 2; attempt <= AliasSuffixLimit; attempt += 1) {
    const suffix = `_${attempt}`;
    const candidate = `${base.slice(0, SafeSqlNameMaxLength - suffix.length)}${suffix}`;
    if (isFree(candidate)) return candidate;
  }

  return fail(`Could not find a free alias for '${desired}'.`, { desired, taken });
}

/** Node kind of any node id, or null when nothing declares it. */
export function nodeKindOf(state, nodeId) {
  if (nodeId === RootNodeId) return state.root ? state.root.kind : null;

  const edge = state.edges.find((candidate) => candidate.id === nodeId);
  return edge ? edge.target : null;
}

/** True when the node is a count/exists edge: it already IS a column, so nothing hangs off it. */
export function isScalarNode(state, nodeId) {
  const edge = state.edges.find((candidate) => candidate.id === nodeId);
  return !!edge && isScalarStrategy(edge.strategy.kind);
}

/**
 * Every node reachable downward from an edge, so removing it can take its subtree with it.
 *
 * The visited set is not decoration. Mutators cannot build a cycle — an edge's parent must
 * already exist and its own id is fresh — but a corrupt stored document could carry one, and
 * without the guard this walk would spin instead of failing.
 */
export function collectDescendantIds(state, edgeId) {
  const visited = new Set([edgeId]);
  const removed = [];
  let frontier = [edgeId];

  while (frontier.length) {
    const children = state.edges
      .filter((edge) => frontier.indexOf(edge.fromNodeId) !== -1 && !visited.has(edge.id))
      .map((edge) => edge.id);

    children.forEach((id) => {
      visited.add(id);
      removed.push(id);
    });
    frontier = children;
  }

  return removed;
}

/**
 * Whether all current and proposed fan-out edges lie on one ancestor-to-descendant path.
 * A candidate with an id replaces that edge (reconfigure); one without an id is a proposed add.
 */
export function fanOutEdgesFormOneChain(state, candidate = null) {
  if (!state || !Array.isArray(state.edges)) return false;

  const edges = state.edges.slice();

  if (candidate) {
    const isNewEdge = !Object.prototype.hasOwnProperty.call(candidate, 'id')
      || candidate.id === null
      || candidate.id === undefined;

    if (isNewEdge) {
      edges.push({ ...candidate, id: Symbol('fanOutCandidate') });
    } else {
      if (typeof candidate.id !== 'string' || !candidate.id.trim()) return false;

      const matches = edges
        .map((edge, index) => (edge && edge.id === candidate.id ? index : -1))
        .filter((index) => index !== -1);
      if (matches.length !== 1) return false;

      const existing = edges[matches[0]];
      edges.splice(matches[0], 1, { ...existing, ...candidate });
    }
  }

  const parents = new Map();
  const fanOutIds = [];

  for (let index = 0; index < edges.length; index += 1) {
    const edge = edges[index];
    const idIsValid = edge && (
      typeof edge.id === 'symbol'
      || (typeof edge.id === 'string' && edge.id.trim() && edge.id !== RootNodeId)
    );
    const parentIsValid = edge
      && typeof edge.fromNodeId === 'string'
      && edge.fromNodeId.trim();
    if (!idIsValid || !parentIsValid || parents.has(edge.id)) return false;

    parents.set(edge.id, edge.fromNodeId);
    if (edge.strategy && edge.strategy.kind === CompositeStrategy.FanOut) {
      fanOutIds.push(edge.id);
    }
  }

  const pathReachesRoot = (edgeId) => {
    const visited = new Set();
    let current = edgeId;

    while (!visited.has(current) && parents.has(current)) {
      visited.add(current);
      const parentId = parents.get(current);
      if (parentId === RootNodeId) return true;
      current = parentId;
    }

    return false;
  };

  if (Array.from(parents.keys()).some((edgeId) => !pathReachesRoot(edgeId))) return false;

  const isAncestor = (ancestorId, descendantId) => {
    const visited = new Set();
    let current = descendantId;

    while (parents.has(current) && !visited.has(current)) {
      visited.add(current);
      const parentId = parents.get(current);
      if (parentId === ancestorId) return true;
      current = parentId;
    }

    return false;
  };

  for (let left = 0; left < fanOutIds.length; left += 1) {
    for (let right = left + 1; right < fanOutIds.length; right += 1) {
      if (!isAncestor(fanOutIds[left], fanOutIds[right])
        && !isAncestor(fanOutIds[right], fanOutIds[left])) return false;
    }
  }

  return true;
}

/** The deepest fan-out node, or the root when no relation changes the grain. */
export function effectiveGrainNodeId(state) {
  const edges = state && Array.isArray(state.edges) ? state.edges : [];
  const parents = new Map(edges
    .filter((edge) => edge && edge.id)
    .map((edge) => [edge.id, edge.fromNodeId]));

  const depthOf = (edgeId) => {
    const visited = new Set();
    let depth = 0;
    let current = edgeId;

    while (parents.has(current) && !visited.has(current)) {
      visited.add(current);
      current = parents.get(current);
      depth += 1;
    }

    return depth;
  };

  return edges
    .filter((edge) => edge.strategy && edge.strategy.kind === CompositeStrategy.FanOut)
    .reduce((deepest, edge) => (
      !deepest || depthOf(edge.id) > depthOf(deepest.id) ? edge : deepest
    ), null)?.id || RootNodeId;
}

/**
 * What an edge is called in the tree, the diagram and in validation messages. A scalar edge is
 * named by the column it produces; every other edge is named by the tag kind it reaches, falling
 * back to the node kind when the target carries no discriminator.
 */
export function describeEdge(edge) {
  if (isScalarStrategy(edge.strategy.kind)) return edge.alias || humanizeNodeKind(edge.target);
  if (edge.discriminator) return edge.discriminator;
  return `${humanizeNodeKind(edge.target)} (All)`;
}

/**
 * Edges parent-first. The server reads the array as its own dependency order and rejects an edge
 * whose parent has not been declared yet, so this pass makes that impossible to get wrong. It is
 * stable: appending is already parent-first by construction, so a definition that was in order
 * comes back in exactly the order it went in.
 */
export function orderEdgesForWire(edges) {
  const declared = new Set([RootNodeId]);
  const ordered = [];
  const pending = edges.slice();

  let progressed = true;
  while (pending.length && progressed) {
    progressed = false;

    for (let index = 0; index < pending.length; index += 1) {
      if (declared.has(pending[index].fromNodeId)) {
        const [edge] = pending.splice(index, 1);
        declared.add(edge.id);
        ordered.push(edge);
        progressed = true;
        index -= 1;
      }
    }
  }

  // An edge whose parent is nowhere is unreachable. It is kept so the server names it in its
  // rejection rather than the definition silently losing a relation the author built.
  if (pending.length) {
    error(`${LogPrefix} edges reference a parent that is not declared`, {
      orphans: pending.map((edge) => edge.id),
    });
  }

  return ordered.concat(pending);
}

export function toWireEdge(edge, definitionVersion) {
  const wire = { id: edge.id, from: edge.fromNodeId, target: edge.target };

  if (definitionVersion === SpatialBindingsDefinitionVersion) {
    wire.traversalKey = edge.traversalKey;
  }
  if (edge.targetId) wire.targetId = edge.targetId;
  if (edge.discriminator) wire.discriminator = edge.discriminator;
  wire.strategy = edge.strategy.kind;
  if (edge.strategy.kind === CompositeStrategy.Latest && edge.strategy.orderBy) {
    wire.orderBy = edge.strategy.orderBy;
  }
  if (edge.strategy.kind === CompositeStrategy.List && edge.strategy.column) {
    wire.column = edge.strategy.column;
  }
  if (isScalarStrategy(edge.strategy.kind) && edge.alias) wire.alias = edge.alias;

  return wire;
}

/** A column's handle. Derived from what it points at, so it survives a load unchanged. */
export function columnIdOf(nodeId, columnName) {
  return `${nodeId}.${columnName}`;
}

/**
 * The first structural problem in the definition, phrased for the author, or null. These are the
 * document-local rules the model can see without the authoring graph — the graph-dependent ones
 * (does this traversal exist, is this edge to-one) stay with the server.
 */
export function describeStructuralProblem(state) {
  const offender = (edge, message) => `The ${describeEdge(edge)} relation ${message}`;

  for (let index = 0; index < state.edges.length; index += 1) {
    const edge = state.edges[index];

    if (edge.strategy.kind === CompositeStrategy.Latest && !edge.strategy.orderBy) {
      return offender(edge, 'needs the column that decides which match is the latest.');
    }

    if (isScalarStrategy(edge.strategy.kind) && !edge.alias) {
      return offender(edge, 'produces one column and must name it.');
    }

  }

  if (!fanOutEdgesFormOneChain(state)) {
    return 'Fan-out relations must form one ancestor-to-descendant chain; sibling branches would multiply rows independently.';
  }

  return null;
}

// ─── Root identity ───────────────────────────────────────────────────────────────────────────

/**
 * Whether the serializer writes the GENERIC root identity (`kind` + `rootId`) instead of the
 * legacy `rootRegisterId`.
 *
 * FLIPPED TRUE 2026-08-18: the DS-11 W1 release train departed — the api's `rootRegisterId`
 * read-alias ships in the same train as this flip, so nothing this wizard writes is ever ahead
 * of the engine that reads it (`.claude/plans/DATA_SOURCE_TASKBOARDS_ENGINE.md`, gate round 3).
 * The switch governs WRITES only. The READ path accepts both shapes permanently, because
 * storage holds a mix forever: definitions written either way keep loading, and editing a
 * legacy-shaped one re-serializes it generic — a per-edit migration, not drift.
 */
export const WRITE_GENERIC_ROOT_IDENTITY = true;

/**
 * Reads a stored root's identity, accepting both the legacy and the generic shape.
 *
 * Legacy `rootRegisterId` implies `register`, because that is the only root kind the format had
 * when those definitions were written. The generic shape carries its own kind and is taken as
 * given — this function must not decide what a root may be, or it becomes the next thing to edit
 * when task boards land.
 *
 * BOTH keys present is refused rather than resolved. The two could name different rows, and every
 * way of picking a winner is a silent guess about which one the author meant; the api refuses it
 * for the same reason.
 *
 * AVA-560 roots: a `finding` root names no id (it is every AI finding of the facility), and a
 * `tag` root's id is the tag kind it reads. Which kinds those are is the vocabulary's call.
 *
 * @returns {{kind: string, rootId: string|null}}
 */
export function readRootIdentity(root) {
  const legacyId = root && root.rootRegisterId;
  const genericId = root && root.rootId;

  if (legacyId && genericId) {
    fail('The stored definition names its root twice, as rootRegisterId and as rootId.', {
      rootRegisterId: legacyId,
      rootId: genericId,
    });
  }

  const kind = (root && root.kind) || CompositeNodeKind.Register;
  const rootId = genericId || legacyId || null;

  if (!rootId && isRootIdentifiedByTagKind(kind)) {
    fail(`The stored definition's ${humanizeNodeKind(kind)} root names no tag kind.`, { root });
  }

  if (!rootId && !isRootWithoutRootId(kind)) {
    fail('The stored definition names no root.', { root });
  }

  return { kind, rootId };
}

/**
 * The root as the wire carries it. One place decides which identity field is emitted, so the
 * eventual switch cannot half-happen.
 *
 * Key order is fixed here as it is everywhere in this serializer — `kind` first, then the
 * identity, then the scope — because byte-stability is what the round-trip checks assert.
 * The id is OMITTED for a root that has none (a `finding` root); every other root kind always
 * carries one, so register and task-board bytes are unchanged.
 */
export function toWireRoot(root, scopeKind) {
  const wire = { kind: root.kind };
  const idField = WRITE_GENERIC_ROOT_IDENTITY ? 'rootId' : 'rootRegisterId';

  if (root.rootRegisterId) wire[idField] = root.rootRegisterId;
  wire.scopeKind = scopeKind;

  return wire;
}

/**
 * Checks a root descriptor against the shape rules of its kind and returns the state's root.
 * `rootRegisterId` keeps its historical name in state whatever the kind: for a tag root it holds
 * the tag kind, for a finding root it is null.
 */
export function toStateRoot(rootDescriptor) {
  const kind = (rootDescriptor && rootDescriptor.kind) || CompositeNodeKind.Register;
  const rootRegisterId = (rootDescriptor && rootDescriptor.rootRegisterId) || null;

  if (!rootRegisterId && isRootIdentifiedByTagKind(kind)) {
    fail(`A ${humanizeNodeKind(kind)} root needs the tag kind it reads.`, { rootDescriptor });
  }

  if (!rootRegisterId && !isRootWithoutRootId(kind)) {
    fail('A root needs the id of the entity it selects (rootRegisterId).', { rootDescriptor });
  }

  if (rootRegisterId && isRootWithoutRootId(kind)) {
    fail(`A ${humanizeNodeKind(kind)} root names no id.`, { rootDescriptor });
  }

  return { kind, rootRegisterId, label: (rootDescriptor && rootDescriptor.label) || null };
}

/**
 * One string per distinct root: its kind and id. Two roots with the same key are the same root,
 * so re-selecting it keeps the relations; any other key is a switch. Also the key the source step
 * selects by and the wizard caches the graph under.
 */
export function rootKeyOf(root) {
  if (!root) return null;
  return `${root.kind || CompositeNodeKind.Register}:${root.rootRegisterId || ''}`;
}

/** What a root is called when no candidate list has named it: its tag kind, else its kind. */
export function describeRoot(root) {
  if (!root) return '';
  if (root.label) return root.label;
  if (isRootIdentifiedByTagKind(root.kind) && root.rootRegisterId) {
    return `${tagDiscriminatorLabel(root.rootRegisterId)} tags`;
  }
  return humanizeNodeKind(root.kind);
}

/**
 * The body of `POST api/data-sources/graph` for a local root: the root's kind, its id when it has
 * one (a tag root's id is its tag kind), and the bridge target when one is chosen.
 */
export function toGraphRequest(root, targetId) {
  const request = { rootKind: root.kind };
  if (root.rootRegisterId) request.rootId = root.rootRegisterId;
  if (targetId) request.targetId = targetId;
  return request;
}
